import cx from 'classix'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ScrollableContainer from '@components/revamp/ScrollableContainer'

import individualsImage1 from '@src/assets/images/home-tabs/individuals-1.jpg'
import individualsImage2 from '@src/assets/images/home-tabs/individuals-2.jpg'
import individualsImage3 from '@src/assets/images/home-tabs/individuals-3.jpg'
import individualsImage4 from '@src/assets/images/home-tabs/individuals-4.jpg'
import organizationsImage1 from '@src/assets/images/home-tabs/organizations-1.jpg'
import organizationsImage2 from '@src/assets/images/home-tabs/organizations-2.jpg'
import organizationsImage3 from '@src/assets/images/home-tabs/organizations-3.jpg'
import organizationsImage4 from '@src/assets/images/home-tabs/organizations-4.jpg'
import i18n from '@src/config/i18n'
import locales from '@src/config/locales'

export default function IndividualsOrganizationsSection() {
  const { t } = useTranslation('revamp')
  const locale = i18n.language
  const [activeTab, setActiveTab] = useState<'individuals' | 'organizations'>('individuals')
  const breakpoints = useBreakpoint()

  const tabs = {
    individuals: [
      {
        title: t('businessPriorities'),
        image: individualsImage1,
        description: t('homeTabs.individuals.businessPrioritiesDescription'),
        url: locales[locale].themesPath,
      },
      {
        title: t('programsMicroprograms'),
        image: individualsImage2,
        description: t('homeTabs.individuals.programsMicroprogramsDescription'),
        url: locales[locale].programsPath,
      },
      {
        title: t('offShelfTrainings'),
        image: individualsImage3,
        description: t('homeTabs.individuals.offShelfTrainingsDescription'),
        url: locales[locale].catalogPath,
      },
      {
        title: t('afiUCircles'),
        image: individualsImage4,
        description: t('homeTabs.individuals.afiUCirclesDescription'),
        url: locales[locale].circlesPath,
      },
    ],
    organizations: [
      {
        title: t('privateDelivery'),
        image: organizationsImage1,
        description: t('homeTabs.organizations.privateDeliveryDescription'),
        url: locales[locale].organizationGroupsPath,
      },
      {
        title: t('learningWorkshops'),
        image: organizationsImage2,
        description: t('homeTabs.organizations.learningWorkshopsDescription'),
        url: locales[locale].organizationWorkshopsPath,
      },
      {
        title: t('businessCoaching'),
        image: organizationsImage3,
        description: t('homeTabs.organizations.businessCoachingDescription'),
        url: locales[locale].organizationCoachingPath,
      },
      {
        title: t('customPrograms'),
        image: organizationsImage4,
        description: t('homeTabs.organizations.customProgramsDescription'),
        url: locales[locale].organizationProgramsPath,
      },
    ],
  }

  const tab = tabs[activeTab]

  const cardWidth = breakpoints.md3 ? 446 : 306

  return (
    <a className='block mt-20 lg3:mt-40 mb-14 lg3:mb-28'>
      <div className='flex flex-wrap items-start gap-x-8 gap-y-4'>
        {[
          { tab: 'individuals', text: t('forIndividuals') },
          { tab: 'organizations', text: t('forOrganizations') },
        ].map(({ tab, text }) => (
          <button
            key={`individuals_organizations_tab_${tab}`}
            className={cx(
              'text-revamp-neutral-700 font-semibold leading-relaxed text-base md3:text-xl lg3:text-3xl '
            )}
            style={{
              borderColor: activeTab === tab ? '#963C4B' : 'transparent',
              borderBottomWidth: 6,
            }}
            onClick={() => setActiveTab(tab as 'individuals' | 'organizations')}
          >
            {text}
          </button>
        ))}
      </div>
      <ScrollableContainer className='flex flex-nowrap gap-8 pt-7 lg3:pt-14'>
        {tab.map(({ title, description, url, image }) => (
          <a className='w-full h-full group' href={url} key={`individuals_organizations_${url}`}>
            <article
              className='bg-light-blue text-revamp-neutral-700 rounded-lg'
              style={{
                flex: `0 0 ${cardWidth}px`,
                height: 468,
              }}
            >
              <div
                className='rounded-t-lg bg-cover'
                style={{
                  backgroundImage: `url(${image})`,
                  backgroundPosition: 'center',
                  height: 255,
                  width: cardWidth,
                }}
              />
              <section className='py-10 px-6 flex flex-col'>
                <a href={url} className='group-hover:underline mb-5'>
                  <h5 className='text-r-2xl font-bold leading-none'>{title}</h5>
                </a>
                <p className='leading-none'>{description}</p>
              </section>
            </article>
          </a>
        ))}
      </ScrollableContainer>
    </a>
  )
}
